import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getMyEmployerDossierSavingsSummaries } from "../../../api/dossier/getMyEmployerDossierSavingsSummaries";
import { getMyEmployerSummaryStats } from "../../../api/dossier/getMyEmployerSummaryStats";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import LimitPaginationContainer from "../../../components/LimitPaginationContainer";
import PageContainer from "../../../components/PageContainer";
import EmployerStatsSummaries from "../../../components/stats/employer/EmployerStatsSummaries";
import { filterAccountsByAnalyticsPermission } from "../../../helpers/user";
import { useAuth } from "../../../providers/AuthProvider";
import { LimitPaginationFilters } from "../../../types/Pagination";
import { User } from "../../../types/User";
import APDossierSavingsSummary, {
  DossierSavingsAmountField,
} from "./APDossierSavingsSummary";

const PAGE_SIZE = 15;

const EmployerAnalyticsDossierSummaries = ({
  me,
  title,
  subtitle,
  amountField,
  amountHeader,
  sortBy,
}: {
  me: User;
  title: string;
  subtitle: string;
  amountField: DossierSavingsAmountField;
  amountHeader: string;
  sortBy: "annualSavings" | "annualisedPayrollTaxSaving";
}) => {
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [filters, setFilters] = useState<LimitPaginationFilters>({
    pageSize: PAGE_SIZE,
    pageNumber: 1,
  });
  const { getScopedPageUrl } = useAuth();

  const authorisedAccounts = me.accountPermissions
    ? filterAccountsByAnalyticsPermission(me.accountPermissions)
    : [];

  const employerSummaryStats = getMyEmployerSummaryStats();
  const dossierSavingsSummaries = getMyEmployerDossierSavingsSummaries(
    { ...filters, sortBy },
    { keepPreviousData: true }
  );

  useEffect(() => {
    if (authorisedAccounts.length > 0) {
      setNavigationCheck(true);
    } else {
      navigate(getScopedPageUrl("home"), { replace: true });
    }
  });

  if (employerSummaryStats.isError) {
    throw employerSummaryStats.error;
  }

  if (dossierSavingsSummaries.isError) {
    throw dossierSavingsSummaries.error;
  }

  const handlePageChange = (pageNumber: number) => {
    setFilters({ ...filters, pageNumber });
  };

  const isLoading =
    employerSummaryStats.isLoading || dossierSavingsSummaries.isLoading;
  const isSuccess =
    employerSummaryStats.isSuccess && dossierSavingsSummaries.isSuccess;

  return (
    <PageContainer
      title={title}
      loading={isLoading || !navigationCheckComplete}
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Typography variant="labelLarge">{subtitle}</Typography>

          <Box pt={2} sx={{ overflowX: "auto" }}>
            <LimitPaginationContainer
              currentPage={filters.pageNumber}
              pageSize={PAGE_SIZE}
              itemCount={dossierSavingsSummaries.data.count}
              onPageChange={handlePageChange}
              isLoading={dossierSavingsSummaries.isFetching}
            >
              <APDossierSavingsSummary
                savingsSummaries={dossierSavingsSummaries.data.items}
                amountField={amountField}
                amountHeader={amountHeader}
              />
            </LimitPaginationContainer>
          </Box>

          <Box>
            <EmployerStatsSummaries
              stats={employerSummaryStats.data}
              showPayrollTaxSavingsCalculation={true}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                pt: 2,
              }}
            >
              <Box sx={{ display: "flex", columnGap: 1, alignItems: "center" }}>
                <BackButton />
                <HomeButton />
              </Box>
              <APContactHelpButton />
            </Stack>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default EmployerAnalyticsDossierSummaries;
